/*
 * Where a field's popover goes, worked out from the room it actually has.
 *
 * The room is the box of the nearest ancestor that clips, not the window: the Work panel scrolls on
 * its own and is narrower than the calendar plus its field's indent, so a popover placed against
 * the window was cut by the pane. In the running app on 2026-10-06 the day-and-time picker lost
 * 20px on the left at 1280 and 1440 wide, which is the whole Monday column, and the proof page it
 * had been checked on has no pane to be cut by (`docs/2026-10-06-picker-inside-its-pane.md`).
 *
 * Pure: boxes in, a placement out. `usePopoverEdge` in `date-time-fields.tsx` does the measuring.
 */

/** A box in viewport coordinates, as `getBoundingClientRect` gives it. */
export interface Box {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface PopoverPlacement {
  /** Opens above the field instead of below it. */
  readonly above: boolean;
  /** Its left edge, in px from the field's left edge. Negative reaches back past the field. */
  readonly shift: number;
  /**
   * How far its bottom would pass the room's bottom when it opens below. More than zero only when
   * it fits on neither side as things stand, and whatever scrolls the room has to make up for it.
   */
  readonly overflowBelow: number;
}

/** The gap between a field and its popover, the same as the stylesheet's. */
export const POPOVER_GAP = 4;
/** The air kept between a popover and the edge of its room, when the room has it to give. */
const POPOVER_MARGIN = 8;

/** The left edge that keeps a popover of this width inside the room, as near the field as it can be. */
function leftInside(field: Box, width: number, room: Box): number {
  const spare = room.right - room.left - width;
  // Wider than the room: nothing fits, so it starts where the room does.
  if (spare <= 0) return room.left;
  // Too little to keep the full margin on both sides: share what there is.
  const margin = Math.min(POPOVER_MARGIN, spare / 2);
  const lowest = room.left + margin;
  const highest = room.right - margin - width;
  return Math.min(Math.max(field.left, lowest), highest);
}

export function placePopover(
  field: Box,
  panel: { readonly width: number; readonly height: number },
  room: Box,
): PopoverPlacement {
  const shift = leftInside(field, panel.width, room) - field.left;
  const bottomBelow = field.bottom + POPOVER_GAP + panel.height;
  const fitsBelow = bottomBelow <= room.bottom - POPOVER_MARGIN;
  if (fitsBelow) return { above: false, shift, overflowBelow: 0 };
  const fitsAbove = field.top - POPOVER_GAP - panel.height >= room.top + POPOVER_MARGIN;
  if (fitsAbove) return { above: true, shift, overflowBelow: 0 };
  // Neither side: below, because a room can be scrolled down to what hangs under it and never up
  // to what sticks out above its start.
  return { above: false, shift, overflowBelow: bottomBelow - (room.bottom - POPOVER_MARGIN) };
}

/** The box a placed popover occupies, for a check that it is inside its room. */
export function placedBox(
  field: Box,
  panel: { readonly width: number; readonly height: number },
  placement: PopoverPlacement,
): Box {
  const left = field.left + placement.shift;
  const top = placement.above
    ? field.top - POPOVER_GAP - panel.height
    : field.bottom + POPOVER_GAP - placement.overflowBelow;
  return { left, top, right: left + panel.width, bottom: top + panel.height };
}
